import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, User, Home, BookOpen, Users, Shield,
  Check, Eye, Lock, Heart, AlertTriangle, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * ROLE-SPECIFIC ONBOARDING (PART B — FINAL LOCKED DIRECTIVE)
 * 
 * Separate onboarding flows for:
 * - Adult (18+)
 * - Parent
 * - Child (<13)
 * - Teen (13–15)
 * - Youth Adult (16–17)
 * - Teacher
 * - Steward
 * 
 * Each flow must:
 * - Explain what the user can and cannot do
 * - Require explicit acknowledgment (checkbox) of boundaries
 * - End at the correct dashboard, not a generic page
 */

const ROLE_CONFIG = {
  adult: {
    title: 'Welcome to LikeKind',
    subtitle: 'Adult Account (18+)',
    icon: User,
    color: 'var(--lk-primary-green)',
    canDo: [
      'Create and join Groups',
      'Join Commons and participate in activities',
      'Create your own Commons (with another Steward)',
      'Connect with local people through activities',
      'Use SCUTA to explore historical timelines',
      'Control your privacy settings'
    ],
    cannotDo: [
      'See or contact children (safety protection)',
      'Override safety rules in any Commons',
      'Post content visible to the public internet'
    ],
    acknowledgment: 'I am 18 years or older. I understand that LikeKind is not a social media platform and I will respect the privacy of other members.',
    dashboard: '/myworld'
  },
  parent: {
    title: 'Welcome to LikeKind',
    subtitle: 'Parent Account',
    icon: Home,
    color: 'var(--lk-secondary-orange)',
    canDo: [
      'Create and manage family accounts for your children',
      'See everything in your child\'s account (Parent Mode)',
      'Approve your child\'s photos and activities',
      'Set screen-time coordination (non-enforcing)',
      'Connect with your child\'s school',
      'Receive all teacher communications'
    ],
    cannotDo: [
      'Enable student-to-student messaging (disabled by design)',
      'Make your child discoverable to strangers',
      'Override school-level safety protections'
    ],
    acknowledgment: 'I am a parent or legal guardian of the child(ren) I will add to my account. I understand my children cannot be discovered by strangers and I will maintain oversight of their accounts.',
    dashboard: '/myworld'
  },
  child: {
    title: 'Welcome to LikeKind',
    subtitle: 'Child Account (Under 13)',
    icon: Heart,
    color: 'var(--lk-secondary-orange)',
    canDo: [
      'See family activities and calendars',
      'Receive messages from teachers',
      'View your Student Notebook',
      'Participate in family-approved activities'
    ],
    cannotDo: [
      'Message other children (for your safety)',
      'Be found by strangers (you\'re not discoverable)',
      'Join groups without parent approval',
      'Post photos without parent approval'
    ],
    acknowledgment: 'I understand that my parent can see everything I do on LikeKind. I will not share personal information with anyone.',
    dashboard: '/myworld',
    requiresParent: true
  },
  teen: {
    title: 'Welcome to LikeKind',
    subtitle: 'Teen Account (13-15)',
    icon: User,
    color: 'var(--lk-secondary-orange)',
    canDo: [
      'See family activities and calendars',
      'Receive messages from teachers',
      'View your Student Notebook',
      'Have some privacy from parents (if they enable it)',
      'Participate in approved activities'
    ],
    cannotDo: [
      'Message other teens directly (unless parent enables)',
      'Be found by strangers outside your school/groups',
      'Create your own groups',
      'Post content publicly'
    ],
    acknowledgment: 'I understand that my parent has visibility into my account. I will respect community guidelines and report anything that makes me uncomfortable.',
    dashboard: '/myworld',
    requiresParent: true
  },
  youth_adult: {
    title: 'Welcome to LikeKind',
    subtitle: 'Youth Adult Account (16-17)',
    icon: User,
    color: 'var(--lk-primary-green)',
    canDo: [
      'Most things adult members can do',
      'Request conversion to full adult account (requires parent approval)',
      'Participate in activities with less oversight',
      'Manage your own privacy settings (with parent visibility)'
    ],
    cannotDo: [
      'Create a Commons (must be 18+)',
      'Fully remove parent visibility without conversion',
      'Access certain adult-only features'
    ],
    acknowledgment: 'I am 16 or 17 years old. I understand I can request conversion to an adult account with my parent\'s approval.',
    dashboard: '/myworld',
    requiresParent: true,
    canConvert: true
  },
  teacher: {
    title: 'Welcome to LikeKind',
    subtitle: 'Teacher Account',
    icon: BookOpen,
    color: 'var(--lk-earth-brown)',
    canDo: [
      'Send homework, announcements, and messages to students',
      'Create and manage class events',
      'View student acknowledgments',
      'Communicate with parents',
      'Access Student Notebooks for your classes'
    ],
    cannotDo: [
      'Enable student-to-student messaging',
      'Access students outside your assigned classes',
      'Bypass parent visibility (parents see all your messages)'
    ],
    acknowledgment: 'I am a teacher or educational staff member at the school that invited me. I understand that all my communications with students are visible to their parents.',
    dashboard: '/myworld'
  },
  steward: {
    title: 'Welcome to LikeKind',
    subtitle: 'Steward Account',
    icon: Shield,
    color: 'var(--lk-earth-brown)',
    canDo: [
      'Manage your Commons or Institution',
      'Approve new members',
      'Create and manage events',
      'Handle reports and safety concerns',
      'Coordinate with your co-steward'
    ],
    cannotDo: [
      'Make unilateral decisions on critical actions',
      'Remove the other steward without their approval',
      'Disable safety features for children',
      'Access member data outside your Commons'
    ],
    acknowledgment: 'I accept the responsibilities of stewardship. I understand that critical actions require approval from both stewards and I will prioritize community safety.',
    dashboard: '/myworld'
  }
};

export default function RoleOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useContext(AuthContext);
  
  const [step, setStep] = useState(1);
  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const role = searchParams.get('role') || 'adult';
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.adult;
  const Icon = config.icon;
  
  const totalSteps = 3;

  useEffect(() => {
    // If user is not authenticated, redirect to auth
    if (!user) {
      navigate(`/auth?role=${role}`);
    }
  }, [user, role, navigate]);

  const handleComplete = async () => {
    if (!acknowledged) {
      toast.error('Please acknowledge the terms to continue');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Update user's onboarding status and role
      await axios.post(`${API}/api/users/complete-onboarding`, {
        role: role,
        acknowledged_at: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
      
      toast.success('Welcome to LikeKind!');
      navigate(config.dashboard);
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      // Still navigate to dashboard even if API fails
      navigate(config.dashboard);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center h-14">
            <button onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-black/5">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div className="flex-1 text-center">
              <h1 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Getting Started</h1>
              <p className="text-xs" style={{ color: 'var(--fy-text-disabled)' }}>Step {step} of {totalSteps}</p>
            </div>
            <div className="w-9" />
          </div>
          {/* Progress bar */}
          <div className="h-1 -mx-4" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
            <div 
              className="h-full transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%`, backgroundColor: config.color }}
            />
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        
        {/* Step 1: Welcome & Role */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${config.color}20` }}
              >
                <Icon className="w-8 h-8" style={{ color: config.color }} />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
                {config.title}
              </h2>
              <p className="text-sm" style={{ color: config.color }}>{config.subtitle}</p>
            </div>

            <Card className="p-6" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
                {role === 'adult' && 'LikeKind is a place for people who want to be present — with their communities, their interests, and their time. This isn\'t a feed designed to pull you in. Everything here is opt-in.'}
                {role === 'parent' && 'As a parent, you have full visibility and control over your children\'s accounts. Your children cannot be discovered by strangers, and you can see everything they do on the platform.'}
                {role === 'child' && 'Welcome! LikeKind is a safe place where you can see family activities and receive messages from your teachers. Your parent can see what you do here to help keep you safe.'}
                {role === 'teen' && 'Welcome! As a teen on LikeKind, you have some independence while staying safe. You can participate in activities and receive school communications. Your parent has some visibility into your account.'}
                {role === 'youth_adult' && 'Welcome! As a youth adult (16-17), you have most of the features of an adult account. When you turn 18, or with parent approval, you can convert to a full adult account.'}
                {role === 'teacher' && 'Welcome! As a teacher, you can communicate with your students through Student Notebooks. Remember: everything you send is visible to parents.'}
                {role === 'steward' && 'Thank you for becoming a steward. You play a critical role in keeping your community safe and organized. Remember: critical actions require approval from both stewards.'}
              </p>
            </Card>

            {config.requiresParent && (
              <div className="p-4 rounded-xl flex items-start gap-3" style={{ backgroundColor: 'rgba(217, 138, 58, 0.1)', border: '1px solid var(--lk-secondary-orange)' }}>
                <Eye className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--lk-secondary-orange)' }} />
                <p className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                  <strong>Parent Visibility:</strong> Your parent can see your activities on LikeKind. This is for your safety.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: What You Can/Cannot Do */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
                What you can do
              </h2>
              <Card className="p-4" style={{ backgroundColor: 'var(--lk-sage-muted)', border: '1px solid var(--lk-primary-green)' }}>
                <ul className="space-y-2">
                  {config.canDo.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                      <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>

            <div>
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
                What you cannot do
              </h2>
              <Card className="p-4" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
                <ul className="space-y-2">
                  {config.cannotDo.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                      <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--fy-text-disabled)' }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>

            {config.canConvert && (
              <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4" style={{ color: config.color }} />
                  <span className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>Future Option</span>
                </div>
                <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  When you turn 18, or with parent approval, you can convert to a full adult account with all features.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Acknowledgment */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
                Confirm &amp; continue
              </h2>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                Please read and acknowledge the following to complete your setup.
              </p>
            </div>

            <Card className="p-6" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="flex items-start gap-3 mb-4">
                <Shield className="w-5 h-5 flex-shrink-0" style={{ color: config.color }} />
                <p className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                  {config.acknowledgment}
                </p>
              </div>
              
              <label className="flex items-start gap-3 p-4 rounded-xl cursor-pointer" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-1"
                  data-testid="acknowledge-checkbox"
                />
                <span className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>
                  I have read and understand the above. I agree to use LikeKind responsibly.
                </span>
              </label>
            </Card>

            {/* Safety Summary */}
            <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--lk-sage-muted)', border: '1px solid var(--lk-primary-green)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4" style={{ color: 'var(--lk-primary-green)' }} />
                <span className="font-medium text-sm" style={{ color: 'var(--lk-primary-green)' }}>Safety First</span>
              </div>
              <p className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                LikeKind is built on safety, not engagement. No algorithms, no engagement tricks, no selling your data.
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="mt-8 flex gap-3">
          {step > 1 && (
            <Button
              onClick={() => setStep(step - 1)}
              variant="outline"
              className="flex-1 h-12 rounded-xl"
              style={{ borderColor: 'var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          
          {step < totalSteps ? (
            <Button
              onClick={() => setStep(step + 1)}
              className="flex-1 h-12 rounded-xl"
              style={{ backgroundColor: config.color, color: 'white' }}
              data-testid="next-step-btn"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={loading || !acknowledged}
              className="flex-1 h-12 rounded-xl"
              style={{ 
                backgroundColor: acknowledged ? config.color : 'var(--fy-surface-muted)',
                color: acknowledged ? 'white' : 'var(--fy-text-disabled)'
              }}
              data-testid="complete-onboarding-btn"
            >
              {loading ? 'Setting up...' : 'Start Using LikeKind'}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
